import { Link } from '@tanstack/react-router';
import { Factory } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { ErrorState } from '@nucleus-commerce/admin-kit/errors';
import { EmptyState, PageContent, PageHeader } from '@nucleus-commerce/admin-kit/layout';
import { usePermissions } from '@nucleus-commerce/admin-kit/permissions';
import type { ProductionBoardItem } from '@nucleus-commerce/api-client/admin';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Button } from '@nucleus-commerce/ui/components/button';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { useWorkflowTemplates } from '@/features/workflows';

import { useProductionBoard } from '../hooks/use-production-board';
import { boardColumns } from '../schemas/board-columns';
import type { ProductionBoardSearch } from '../schemas/production-board-search';

import { BoardToolbar } from './board-toolbar';
import { OrderLabelChips } from './order-label-chips';
import { DelayedBadge, StageStatusBadge } from './stage-status-badge';

function BoardCard({
  storeId,
  item,
  first,
}: {
  storeId: string;
  item: ProductionBoardItem;
  /** The board tour points at the first card only. */
  first: boolean;
}) {
  const { t } = useTranslation('production');
  const format = useFormat();
  return (
    <li>
      <Link
        to="/s/$storeId/orders/$orderId"
        params={{ storeId, orderId: item.orderId }}
        aria-label={t('board.card', { order: item.orderName, stage: item.stageName ?? '—' })}
        data-tour={first ? 'production.board.card' : undefined}
        className={cn(
          'bg-card hover:border-primary/60 flex flex-col gap-1.5 rounded-md border p-3 text-sm transition-colors',
          item.delayed && 'border-destructive/50',
        )}
      >
        <span className="flex items-center justify-between gap-2">
          <span className="font-mono font-medium">{item.orderName}</span>
          {item.stageStatus ? <StageStatusBadge status={item.stageStatus} /> : null}
        </span>
        <span className="truncate">{item.customerName ?? item.email}</span>
        {item.dueAt ? (
          <span
            className={cn(
              'text-xs',
              item.delayed ? 'text-destructive font-medium' : 'text-muted-foreground',
            )}
            title={format.utc(item.dueAt)}
          >
            {t('board.due', { when: format.dateTime(item.dueAt) })}
          </span>
        ) : null}
        <span className="flex flex-wrap gap-1">
          {item.delayed && !item.labels.includes('delayed') ? <DelayedBadge /> : null}
          <OrderLabelChips labels={item.labels} />
        </span>
      </Link>
    </li>
  );
}

export interface ProductionBoardViewProps {
  store: { id: string; name: string };
  search: ProductionBoardSearch;
  onSearchChange: (patch: Partial<ProductionBoardSearch>) => void;
}

/**
 * Columns per stage of the store's default template with a card per order, filtered by the
 * search the page keeps in the URL. Pages of the endpoint are appended with "Load more".
 */
export function ProductionBoardView({ store, search, onSearchChange }: ProductionBoardViewProps) {
  const { t } = useTranslation('production');
  const { can } = usePermissions();
  const templates = useWorkflowTemplates(store.id, { enabled: can('settings.read') });
  const template = templates.data?.find((tpl) => tpl.isDefault);
  const query = useProductionBoard(store.id, search);
  const items = query.data?.pages.flatMap((page) => page.items) ?? [];
  const columns = boardColumns(items, template, t('board.otherStage')).filter(
    (column) => !search.stage || column.key === search.stage,
  );
  const stageOptions = template
    ? template.stages.map((s) => ({ key: s.key, name: s.name }))
    : columns.flatMap((c) => (c.key ? [{ key: c.key, name: c.name }] : []));
  const filtered = [search.q, search.stage, search.label].some(Boolean);
  const firstOrderId = columns.find((column) => column.items.length > 0)?.items[0]?.orderId;

  return (
    <PageContent>
      <PageHeader
        title={t('board.title')}
        description={t('board.description', { store: store.name })}
      />
      <BoardToolbar search={search} stages={stageOptions} onChange={onSearchChange} />
      {query.isError ? (
        <ErrorState error={query.error} onRetry={() => void query.refetch()} />
      ) : query.isPending ? (
        <div className="flex gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-64 w-64 shrink-0" />
          ))}
        </div>
      ) : items.length === 0 && !filtered ? (
        <EmptyState
          icon={Factory}
          title={t('board.empty')}
          tour="production.board.empty"
          tourLink
        />
      ) : (
        <>
          {items.length === 0 ? (
            <p className="text-muted-foreground text-sm">{t('board.noMatches')}</p>
          ) : null}
          <div className="overflow-x-auto pb-2">
            <ul
              className="flex min-w-max gap-3"
              aria-label={t('board.columnsLabel')}
              data-tour="production.board.columns"
            >
              {columns.map((column) => (
                <li
                  key={column.key ?? '__other__'}
                  aria-label={column.name}
                  className="bg-muted/40 flex w-64 shrink-0 flex-col gap-2 rounded-lg border p-2"
                >
                  <h2 className="px-1 text-sm font-semibold">
                    {t('board.column', { name: column.name, count: column.items.length })}
                  </h2>
                  <ul className="flex flex-col gap-2">
                    {column.items.map((item) => (
                      <BoardCard
                        key={item.orderId}
                        storeId={store.id}
                        item={item}
                        first={item.orderId === firstOrderId}
                      />
                    ))}
                  </ul>
                </li>
              ))}
            </ul>
          </div>
          {query.hasNextPage ? (
            <Button
              type="button"
              variant="outline"
              className="self-center"
              disabled={query.isFetchingNextPage}
              onClick={() => void query.fetchNextPage()}
            >
              {query.isFetchingNextPage ? t('board.loadingMore') : t('board.loadMore')}
            </Button>
          ) : null}
        </>
      )}
    </PageContent>
  );
}
